import { useCallback, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import { TopRevisionsButton } from '@/components';
import { AddLocationDialog } from '@/components/common/add-location-dialog';
import { AddTransactionDialog } from '@/components/common/add-transaction-dialog';
import { EditLocationDialog } from '@/components/common/edit-location-dialog';
import { EditOwnerDialog } from '@/components/common/edit-owner-dialog';
import { EditSemenDialog } from '@/components/common/edit-semen-dialog';
import { EditSireDialog } from '@/components/common/edit-sire-dialog';
import { EditSireFormValues } from '@/components/common/edit-sire-form';
import { NewSemenDialog } from '@/components/common/new-semen-dialog';
import { NewSireDialog } from '@/components/common/new-sire-dialog/NewSireDialog';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { SplitButton } from '@/components/ui/split-button';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSemenAppContext } from '@/pages/semen-app/context/SemenAppContext';
import { useSelectedSemenInventoryReport } from '@/services/reports';
import { SemenDto } from '@/services/semen-service';
import { Sire } from '@/types.gql';

type SemenActionsProps = {
  gridRef: React.RefObject<AgGridReact> | null | undefined;
  onFilterChanged: () => void;
  reportSemenIds?: string[];
};

export const SemenActions = ({
  gridRef,
  onFilterChanged,
  reportSemenIds,
}: SemenActionsProps) => {
  const { t } = useTranslation();
  const isSelectedSemenShippingEnabled = useFeatureFlag(
    'Selected_Semen_Shipping_Integration',
  );
  const isPersistSemenAcrossTabsEnabled = useFeatureFlag(
    'Open_New_Shipment_in_New_Window',
  );
  const isSemenInventoryReportEnabled = useFeatureFlag(
    'Semen_Inventory_Report',
  );
  const {
    selectedSemen,
    selectedSemenRef,
    filters: _filters,
    semenData,
    setSelectedSemen,
    addToShippingSemen,
  } = useSemenAppContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [generateSemenInventory, semenInventoryReport] =
    useSelectedSemenInventoryReport();

  const [editSemenDialogVisible, setEditSemenDialogVisible] = useState(false);
  const [addTransactionDialogVisible, setAddTransactionDialogVisible] =
    useState(false);
  const [topRevisionsDialogVisible, setTopRevisionsDialogVisible] =
    useState(false);

  const [
    editStorageLocationDialogVisible,
    setEditStorageLocationDialogVisible,
  ] = useState<boolean>(false);

  const [editOwnerDialogVisible, setEditOwnerDialogVisible] =
    useState<boolean>(false);

  const [addSemenDialogVisible, setAddSemenDialogVisible] = useState(false);
  const [addLocationDialogVisible, setAddLocationDialogVisible] =
    useState(false);
  const [addSireDialogVisible, setAddSireDialogVisible] = useState(false);
  const [editSireDialogVisible, setEditSireDialogVisible] = useState(false);
  const [revisionComment, setRevisionComment] = useState('');
  const [revisionNumber, setRevisionNumber] = useState({
    previousRevision: '',
    currentRevision: '',
  });

  const handleViewDetail = useCallback(() => {
    if (selectedSemen.length === 0) return;
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('semenId', selectedSemen[0].id);
    const url = `/semen/detail?${urlParams.toString()}`;
    window.open(url, '_blank');
  }, [selectedSemen, location.search]);

  const handleGoHome = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete('semenId');
    const search = urlParams.toString();

    setSelectedSemen([]);
    navigate(`/semen${search ? `?${search}` : ''}`, { replace: true });
  }, [location.search, navigate, setSelectedSemen]);

  const handleOpenDonorSchedule = useCallback(() => {
    const firstSemen = selectedSemen[0];
    if (!firstSemen) return;

    if (firstSemen.donorScheduleActive === false) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('donorScheduling.donorScheduleNotActive'),
          detail: t('donorScheduling.donorScheduleNotActiveDetail'),
          life: ToastLife.DEFAULT,
        }),
      );
      return;
    }
    if (firstSemen.id) {
      window.open(`/donor-scheduling`, '_blank');
    }
  }, [selectedSemen, dispatch, t]);

  const handleEditStorageLocation = useCallback(() => {
    if (selectedSemen.length === 0) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('common.noSelection'),
          detail: t('grid.selectAtLeastOneRow'),
          life: ToastLife.DEFAULT,
        }),
      );
      return;
    }

    setEditStorageLocationDialogVisible(true);
  }, [dispatch, t, selectedSemen]);

  const handleEditOwner = useCallback(() => {
    if (selectedSemen.length === 0) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('common.noSelection'),
          detail: t('grid.selectAtLeastOneRow'),
          life: ToastLife.DEFAULT,
        }),
      );
      return;
    }

    setEditOwnerDialogVisible(true);
  }, [dispatch, t, selectedSemen]);

  const handleSaveSire = useCallback(
    async (_data: EditSireFormValues) => {
      setEditSireDialogVisible(false);

      await queryClient.refetchQueries({
        queryKey: ['semen'],
      });

      if (selectedSemen.length > 0 && semenData) {
        const updatedSemen = semenData.find(
          (semen) => semen.id === selectedSemen[0].id,
        );
        if (updatedSemen) {
          setSelectedSemen([updatedSemen]);
        }
      }
    },
    [queryClient, selectedSemen, semenData, setSelectedSemen],
  );

  const viewMenuItems = useMemo(() => {
    const isOnDetailPage = location.pathname.includes('/semen/detail');
    return [
      {
        label: isOnDetailPage
          ? t('home_page.goToHome')
          : t('home_page.viewDetail'),
        icon: 'pi pi-eye',
        command: isOnDetailPage ? handleGoHome : handleViewDetail,
        title: isOnDetailPage
          ? t('home_page.willOpenHomePage')
          : t('home_page.willOpenDetailPage'),
        disabled: !isOnDetailPage && selectedSemen.length !== 1,
      },
      {
        label: t('donorScheduling.openDonorSchedule'),
        icon: 'pi pi-eye',
        command: handleOpenDonorSchedule,
        title: t('donorScheduling.willOpenDonorSchedule'),
        disabled: selectedSemen.length !== 1,
      },
      {
        label: t('revision.viewTopRevisions'),
        icon: 'pi pi-eye',
        command: () => setTopRevisionsDialogVisible(true),
        disabled:
          selectedSemen.length !== 1 || !selectedSemen[0]?.donorScheduleDate,
      },
    ];
  }, [
    handleGoHome,
    handleViewDetail,
    handleOpenDonorSchedule,
    selectedSemen,
    setTopRevisionsDialogVisible,
    t,
    location.pathname,
  ]);

  const handleAddToShipment = useCallback(() => {
    const gridSelectedRows = gridRef?.current?.api?.getSelectedRows() as
      | SemenDto[]
      | undefined;
    const semenToShip =
      gridSelectedRows && gridSelectedRows.length > 0
        ? gridSelectedRows
        : selectedSemen.length > 0
          ? selectedSemen
          : selectedSemenRef.current;

    if (isPersistSemenAcrossTabsEnabled) {
      addToShippingSemen(semenToShip);
      const params = new URLSearchParams(location.search);
      params.set('tab', 'shipping-integration');
      setSearchParams(params, { replace: true });
    } else if (isSelectedSemenShippingEnabled) {
      const search = location.search;
      navigate(`/semen/shipping-integration${search}`, {
        state: { shippingSemen: semenToShip },
      });
    } else {
      navigate('/semen/shipping-integration');
    }
  }, [
    navigate,
    setSearchParams,
    addToShippingSemen,
    isPersistSemenAcrossTabsEnabled,
    isSelectedSemenShippingEnabled,
    selectedSemenRef,
    selectedSemen,
    gridRef,
    location.search,
  ]);

  const handleEditSemen = useCallback(() => {
    setEditSemenDialogVisible(true);
  }, []);

  const handleGenerateSemenInventory = useCallback(() => {
    return generateSemenInventory({
      semenIds: reportSemenIds ?? selectedSemen.map((selected) => selected.id),
    });
  }, [generateSemenInventory, reportSemenIds, selectedSemen]);

  const reportSemenCount = reportSemenIds?.length ?? selectedSemen.length;

  const printMenuItems = useMemo(
    () => [
      {
        label: 'Semen Inventory Report',
        icon: 'pi pi-print',
        command: handleGenerateSemenInventory,
        disabled: reportSemenCount === 0 || semenInventoryReport.isPending,
      },
    ],
    [
      handleGenerateSemenInventory,
      reportSemenCount,
      semenInventoryReport.isPending,
    ],
  );

  const editMenuItems = useMemo(
    () => [
      {
        label: t('semen.editSemen'),
        icon: 'pi pi-pencil',
        command: handleEditSemen,
        disabled: selectedSemen.length === 0,
      },
      {
        label: t('sire.editSire'),
        icon: 'pi pi-pencil',
        command: () => setEditSireDialogVisible(true),
        disabled: selectedSemen.length !== 1,
      },
      {
        label: t('storageLocation.editLocation'),
        icon: 'pi pi-pencil',
        command: handleEditStorageLocation,
        disabled: selectedSemen.length === 0,
      },
      {
        label: t('owner.editOwner'),
        icon: 'pi pi-pencil',
        command: handleEditOwner,
        disabled: selectedSemen.length === 0,
      },
    ],
    [
      handleEditSemen,
      selectedSemen,
      handleEditStorageLocation,
      handleEditOwner,
      t,
    ],
  );

  const addMenuItems = useMemo(() => {
    const isOnDetailPage = location.pathname.includes('/semen/detail');
    return [
      {
        label: t('semen.addSemen'),
        icon: 'pi pi-plus',
        command: () => setAddSemenDialogVisible(true),
      },
      {
        label: t('storageLocation.addLocation'),
        icon: 'pi pi-plus',
        command: () => setAddLocationDialogVisible(true),
      },
      {
        label: t('sire.addSire'),
        icon: 'pi pi-plus',
        command: () => setAddSireDialogVisible(true),
      },
      {
        label: t('semenTransaction.addSemenTransaction'),
        icon: 'pi pi-plus',
        command: () => setAddTransactionDialogVisible(true),
        disabled: isOnDetailPage ? false : selectedSemen.length < 1,
      },
      {
        label: t('shipping.addToShipment'),
        icon: 'pi pi-arrow-right',
        command: handleAddToShipment,
        disabled: selectedSemen.length === 0,
      },
    ];
  }, [
    setAddSemenDialogVisible,
    setAddLocationDialogVisible,
    setAddSireDialogVisible,
    selectedSemen.length,
    location.pathname,
    handleAddToShipment,
    t,
  ]);

  return (
    <div data-testid="semen-actions">
      <div className="display mb-4 flex flex-wrap gap-2">
        <SplitButton
          label={t('common.add')}
          icon="pi pi-plus"
          items={addMenuItems}
          onClick={() => setAddSemenDialogVisible(true)}
        />
        <SplitButton
          label={t('common.view')}
          icon="pi pi-eye"
          items={viewMenuItems}
          disabled={selectedSemen.length === 0}
        />
        <SplitButton
          label={t('common.edit')}
          icon="pi pi-pencil"
          items={editMenuItems}
          disabled={selectedSemen.length === 0}
        />
        {isSemenInventoryReportEnabled && (
          <SplitButton
            label="Print"
            icon="pi pi-print"
            items={printMenuItems}
            onClick={handleGenerateSemenInventory}
            disabled={reportSemenCount === 0 || semenInventoryReport.isPending}
          />
        )}
        <TopRevisionsButton
          label={t('revision.topRevisions')}
          icon="pi pi-eye"
          size="small"
          tooltip={t('revision.showAllTopRevisionNumbersPerFertSite')}
          date={selectedSemen[0]?.donorScheduleDate}
          disabled={!selectedSemen[0]?.donorScheduleDate}
          hideTrigger={true}
          visible={topRevisionsDialogVisible}
          onHide={() => setTopRevisionsDialogVisible(false)}
        />
        {editSemenDialogVisible && (
          <EditSemenDialog
            visible={editSemenDialogVisible}
            onHide={() => setEditSemenDialogVisible(false)}
            header={
              selectedSemen.length > 1
                ? 'Bulk Edit Semen'
                : 'Edit Semen Information'
            }
            className="w-[90vw] max-w-[1400px]"
            style={{ height: '90vh' }}
            semen={selectedSemen.length === 1 ? selectedSemen[0] : undefined}
            rows={selectedSemen.length > 1 ? selectedSemen : undefined}
            onCancel={() => {
              setEditSemenDialogVisible(false);
              gridRef?.current?.api.deselectAll();
              setTimeout(() => {
                gridRef?.current?.api.refreshServerSide({ purge: true });
                onFilterChanged();
              }, 200);
            }}
            onSuccess={() => {
              setEditSemenDialogVisible(false);
              gridRef?.current?.api.deselectAll();
              setTimeout(() => {
                gridRef?.current?.api.refreshServerSide({ purge: true });
                onFilterChanged();
              }, 200);
            }}
          />
        )}
        <AddTransactionDialog
          gridRef={gridRef || undefined}
          visible={addTransactionDialogVisible}
          semenId={selectedSemen[0]?.id || ''}
          semen={selectedSemen[0] || null}
          semens={selectedSemen.length > 1 ? selectedSemen : undefined}
          onHide={() => setAddTransactionDialogVisible(false)}
          onSuccess={() => {
            gridRef?.current?.api.deselectAll();
            setTimeout(() => {
              gridRef?.current?.api.refreshServerSide({ purge: true });
              onFilterChanged();
            }, 200);
          }}
        />
        <NewSemenDialog
          visible={addSemenDialogVisible}
          onHide={() => setAddSemenDialogVisible(false)}
          onSuccess={() => {
            gridRef?.current?.api?.refreshServerSide({ purge: true });
          }}
        />
        <AddLocationDialog
          gridRef={gridRef || undefined}
          visible={addLocationDialogVisible}
          onHide={() => setAddLocationDialogVisible(false)}
        />
        <NewSireDialog
          visible={addSireDialogVisible}
          onHide={() => setAddSireDialogVisible(false)}
          gridRef={gridRef || undefined}
          revisionComment={revisionComment}
          setRevisionComment={setRevisionComment}
          revisionNumber={revisionNumber}
          setRevisionNumber={setRevisionNumber}
        />
      </div>

      <EditLocationDialog
        visible={editStorageLocationDialogVisible}
        onHide={() => setEditStorageLocationDialogVisible(false)}
        selectedSemen={selectedSemen}
        gridRef={gridRef || undefined}
        onFilterChanged={onFilterChanged}
      />

      <EditOwnerDialog
        visible={editOwnerDialogVisible}
        onHide={() => setEditOwnerDialogVisible(false)}
        selectedSemen={selectedSemen}
        gridRef={gridRef || undefined}
        onFilterChanged={onFilterChanged}
      />

      {editSireDialogVisible && (
        <EditSireDialog
          visible={editSireDialogVisible}
          onHide={() => setEditSireDialogVisible(false)}
          onSave={handleSaveSire}
          sire={selectedSemen[0]?.sire as Sire}
        />
      )}
    </div>
  );
};
